import { useEffect, useState } from 'react';
import { RequestFailed } from '../api/client';
import { useApp } from '../state/AppState';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { NewGroupPanel } from './NewGroupPanel';
import { AddMembersPanel } from './AddMembersPanel';
import { PersonSearch } from './PersonSearch';
import { InvitePanel } from './InvitePanel';
import { UpdatePanel } from './UpdatePanel';
import type { Chat, Person } from '../api/types';

export function ChatScreen() {
  const app = useApp();
  const {
    session, status, chats, contacts, onlineUserIds, activeChatId,
    messages, pending, typingUserIds, historyExhausted,
  } = app;

  const [mobileList, setMobileList] = useState(true);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  /** Ошибки действий с группой: раньше они терялись молча. */
  const [actionError, setActionError] = useState<string | null>(null);

  const activeChat = chats.find((chat) => chat.id === activeChatId) ?? null;

  /**
   * Выход из группы.
   *
   * Отдельной функцией, потому что обязателен разбор ошибки: раньше отказ
   * сервера пропадал в никуда, и нажатие выглядело как полное бездействие.
   */
  async function leaveGroup(chat: Chat) {
    if (!confirm(`Выйти из группы «${chat.title}»?`)) return;
    setActionError(null);
    try {
      await app.leaveChat(chat.id);
      // Возврат к списку обязателен: на телефоне панель чата занимает весь
      // экран, а кнопка «← чаты» рисуется только при выбранном чате
      setMobileList(true);
    } catch (e) {
      setActionError(e instanceof RequestFailed ? e.error.message : 'Не удалось выйти из группы');
    }
  }

  // Чат мог исчезнуть под ногами: из группы вышли с другого устройства или
  // её удалили. На телефоне это оставляло человека на пустом экране без
  // навигации, потому что список чатов скрыт, а кнопка возврата не рисуется.
  useEffect(() => {
    if (!activeChat && !mobileList) {
      setMobileList(true);
    }
  }, [activeChat, mobileList]);

  useEffect(() => {
    setAddingMembers(false);
    setMenuOpen(false);
    setActionError(null);

    // Снимаем фокус с поля ввода при входе в чат. Переход из уведомления
    // возвращал приложение с восстановленным фокусом, и клавиатура
    // открывалась сама — занимая половину экрана ещё до того, как человек
    // собрался что-то писать.
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && focused !== document.body) {
      focused.blur();
    }
  }, [activeChatId]);

  const nameOf = (userId: string | null): string => {
    if (!userId) return 'система';
    const member = activeChat?.members.find((m) => m.userId === userId);
    if (member) return member.displayName;
    return contacts.find((p) => p.id === userId)?.displayName ?? 'неизвестный';
  };

  function openChat(chatId: string) {
    app.selectChat(chatId);
    setMobileList(false);
  }

  async function openPerson(person: Person) {
    await app.startDirectChat(person.id);
    setMobileList(false);
  }

  const statusLabel: Record<typeof status, string> = {
    idle: 'не подключено',
    connecting: 'соединяемся…',
    online: 'на связи',
    offline: 'нет связи',
    unauthorized: 'сессия истекла',
  };

  return (
    <div className={'layout' + (mobileList ? ' show-list' : '')}>
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="who">
            <strong>{session?.user.displayName}</strong>
            <span>@{session?.user.username}</span>
          </div>
          <span className={'conn ' + status}>{statusLabel[status]}</span>
          {session?.user.role === 'ADMIN' && (
            <button
              className="icon-button"
              onClick={() => { setInviting((value) => !value); setCreatingGroup(false); }}
              title="Пригласить человека"
              aria-label="Пригласить человека"
            >
              <InviteIcon />
            </button>
          )}
          <button
            className="icon-button"
            onClick={() => { setCreatingGroup((value) => !value); setInviting(false); }}
            title="Новая группа"
            aria-label="Новая группа"
          >
            <GroupIcon />
          </button>
          <button
            className="icon-button"
            onClick={() => { setUpdating((value) => !value); setInviting(false); setCreatingGroup(false); }}
            title="Настройки: уведомления и обновление"
            aria-label="Настройки"
          >
            <UpdateIcon />
          </button>
          <button
            className="icon-button"
            onClick={() => void app.logout()}
            title="Выйти"
            aria-label="Выйти"
          >
            <PowerIcon />
          </button>
        </div>

        <div className="scroll">
          {updating && session && (
            <UpdatePanel deviceId={session.deviceId} onClose={() => setUpdating(false)} />
          )}

          {inviting && <InvitePanel onClose={() => setInviting(false)} />}

          {creatingGroup && (
            <NewGroupPanel
              contacts={contacts}
              onSearch={app.searchPeople}
              onCancel={() => setCreatingGroup(false)}
              onCreate={async (title, memberIds) => {
                await app.createGroup(title, memberIds);
                setCreatingGroup(false);
                setMobileList(false);
              }}
            />
          )}

          {chats.length > 0 && <div className="section-title">Переписки</div>}
          {chats.map((chat) => (
            <ChatRow
              key={chat.id}
              chat={chat}
              active={chat.id === activeChatId}
              online={isChatOnline(chat, session?.user.id, onlineUserIds)}
              onClick={() => openChat(chat.id)}
            />
          ))}

          <div className="section-title">Начать переписку</div>
          <PersonSearch onSearch={app.searchPeople} onPick={(person) => void openPerson(person)} />

          {chats.length === 0 && (
            <p className="search-hint">
              Пока переписок нет. Найдите человека по имени или пригласите нового.
            </p>
          )}
        </div>
      </aside>

      <main className="chat">
        {status !== 'online' && status !== 'idle' && (
          <div className="banner">{statusLabel[status]} — сообщения отправятся, когда связь вернётся</div>
        )}

        {activeChat ? (
          <>
            <div className="chat-header">
              <button className="ghost mobile-only" onClick={() => setMobileList(true)}>
                ← чаты
              </button>
              <h2>{activeChat.title}</h2>
              <span className="hint">
                {activeChat.type === 'GROUP'
                  ? plural(activeChat.members.length, 'участник', 'участника', 'участников')
                  : isChatOnline(activeChat, session?.user.id, onlineUserIds) ? 'в сети' : ''}
              </span>
              {/*
                Действия спрятаны в меню: двумя кнопками подряд заголовок
                на телефоне разъезжался в три строки — название, счётчик
                и кнопки налезали друг на друга.
              */}
              {activeChat.type === 'GROUP' && (
                <div className="header-menu">
                  <button
                    className="icon-button"
                    aria-label="Действия с группой"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((value) => !value)}
                  >
                    <DotsIcon />
                  </button>
                  {menuOpen && (
                    <>
                      <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
                      <div className="menu">
                        <button
                          className="menu-item"
                          onClick={() => { setAddingMembers(true); setMenuOpen(false); }}
                        >
                          добавить людей
                        </button>
                        <button
                          className="menu-item danger"
                          onClick={() => { setMenuOpen(false); void leaveGroup(activeChat); }}
                        >
                          выйти из группы
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {actionError && (
              <p className="error" style={{ margin: '8px 16px 0' }}>{actionError}</p>
            )}

            {addingMembers && activeChat.type === 'GROUP' && (
              <AddMembersPanel
                chat={activeChat}
                contacts={contacts}
                onSearch={app.searchPeople}
                onAdd={app.addMembers}
                onClose={() => setAddingMembers(false)}
              />
            )}

            <MessageList
              chatId={activeChat.id}
              messages={messages}
              pending={pending}
              currentUserId={session?.user.id ?? ''}
              showAuthors={activeChat.type === 'GROUP'}
              authorName={nameOf}
              readSeq={readSeq(activeChat, session?.user.id)}
              onReachTop={() => void app.loadOlder()}
              onDelete={app.deleteMessage}
              onReply={app.setReplyTo}
              onReact={app.toggleReaction}
              onAnswerBot={app.sendMessage}
              historyExhausted={historyExhausted}
            />

            <div className="typing">
              {typingUserIds.length > 0 && `${nameOf(typingUserIds[0] ?? null)} печатает…`}
            </div>

            <Composer
              disabled={status !== 'online'}
              replyTo={app.replyTo}
              replyAuthor={nameOf(app.replyTo?.senderId ?? null)}
              onCancelReply={() => app.setReplyTo(null)}
              onSend={app.sendMessage}
              onTyping={app.notifyTyping}
            />
          </>
        ) : (
          <div className="empty">
            <div>
              <p>Выберите переписку слева или начните новую.</p>
              {session?.user.role === 'ADMIN' && (
                <p className="sub">
                  Чтобы позвать человека, нажмите значок с плюсом слева сверху.
                </p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ChatRow({ chat, active, online, onClick }: {
  chat: Chat; active: boolean; online: boolean; onClick: () => void;
}) {
  return (
    <button className={'row' + (active ? ' active' : '')} onClick={onClick}>
      <span className="avatar">
        {initials(chat.title)}
        {online && <span className="dot" />}
      </span>
      <span className="body">
        <span className="name">{chat.title}</span>
      </span>
      {chat.unread > 0 && <span className="badge">{chat.unread}</span>}
    </button>
  );
}

/** Русские числительные: 1 участник, 2 участника, 5 участников. */
/** Стрелка по кругу — привычный значок обновления. */
function UpdateIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12a8 8 0 1 1-2.3-5.6" />
      <path d="M20 4v4h-4" />
    </svg>
  );
}

/** Три точки — привычный значок «здесь есть ещё действия». */
function DotsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}

function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${count} ${many}`;
  if (mod10 === 1) return `${count} ${one}`;
  if (mod10 >= 2 && mod10 <= 4) return `${count} ${few}`;
  return `${count} ${many}`;
}

/** Значок приглашения: фигура человека с плюсом. */
function InviteIcon() {
  return (
    <svg
      viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
    >
      <circle cx="10" cy="8" r="3.4" />
      <path d="M4 19c0-3.1 2.7-5.2 6-5.2 1.1 0 2.1.2 3 .6" />
      <path d="M17 15v6M14 18h6" />
    </svg>
  );
}

/** Значок группы: две фигуры рядом. */
function GroupIcon() {
  return (
    <svg
      viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
    >
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16.5 6.6a3 3 0 0 1 0 5.8" />
      <path d="M17.5 14.4c2 .6 3.5 2.3 3.5 4.6" />
    </svg>
  );
}

/** Значок выключения. Рисуем сами, чтобы не тянуть библиотеку иконок ради одной. */
function PowerIcon() {
  return (
    <svg
      viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    >
      <path d="M12 3.5v8.5" />
      <path d="M7.6 6.9a7 7 0 1 0 8.8 0" />
    </svg>
  );
}

/**
 * До какого номера сообщение прочитал хотя бы кто-то из собеседников.
 *
 * Берётся максимум. Раньше был минимум — «дошло до каждого», — но в группе
 * это означало, что галочки не загорятся, пока не заглянет самый занятой
 * участник: сообщение давно прочитано половиной людей, а на вид не дошло
 * ни до кого. Владелец попросил считать прочитанным по первому.
 */
function readSeq(chat: Chat, selfId: string | undefined): number {
  const others = chat.members.filter((member) => member.userId !== selfId);
  if (others.length === 0) return 0;
  return Math.max(...others.map((member) => member.lastReadSeq));
}

function isChatOnline(chat: Chat, selfId: string | undefined, online: Set<string>): boolean {
  return chat.members.some((member) => member.userId !== selfId && online.has(member.userId));
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '?';
}
